import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from 'react';

import {
  MAX_BACKUP_FILE_BYTES,
  type BackupCounts,
  type BackupIssue,
  type BackupOperations,
  type CreatedJsonBackup,
  type JsonRestorePreview,
  type VerifiedCurrentJsonBackup,
} from './types';

type BusyOperation = 'create' | 'preview' | 'restore' | 'verify';

interface RestoreStage {
  readonly fileName: string;
  readonly preview: JsonRestorePreview;
  readonly sourceBytes: Uint8Array;
}

type RestoreOutcome =
  | {
      readonly detail: string;
      readonly kind: 'error';
      readonly title: string;
    }
  | {
      readonly datasetEpoch: string;
      readonly kind: 'success';
      readonly reopenRequired: boolean;
      readonly title: string;
    };

export interface BackupPanelProps {
  readonly maxFileBytes?: number;
  readonly operations: BackupOperations;
  readonly reloadApplication?: () => void;
}

const COUNT_LABELS: Readonly<Record<string, string>> = {
  location_merge_decisions: 'Rozhodnutí o sloučení míst',
  route_templates: 'Šablony tras',
  route_variants: 'Varianty tras',
  shifts: 'Směny',
  trip_revisions: 'Revize úseků',
  trip_segments: 'Úseky',
  user_defined_locations: 'Vlastní provozní místa',
  vehicles: 'Vozidla',
};

function effectiveFileLimit(requestedLimit: number | undefined): number {
  if (requestedLimit === undefined) {
    return MAX_BACKUP_FILE_BYTES;
  }
  if (!Number.isFinite(requestedLimit) || requestedLimit < 1) {
    return MAX_BACKUP_FILE_BYTES;
  }
  return Math.min(Math.floor(requestedLimit), MAX_BACKUP_FILE_BYTES);
}

function countLabel(key: string): string {
  return COUNT_LABELS[key] ?? key.replaceAll('_', ' ');
}

function errorDetail(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}

function fileSizeError(file: File, maxFileBytes: number): string | null {
  if (file.size <= maxFileBytes) {
    return null;
  }
  const maximumMebibytes = maxFileBytes / (1024 * 1024);
  return `Soubor ${file.name} má ${file.size.toLocaleString('cs-CZ')} B. Maximum je ${maximumMebibytes.toLocaleString('cs-CZ')} MiB; soubor nebyl načten.`;
}

async function readFileBytes(file: File, maxFileBytes: number): Promise<Uint8Array> {
  const sizeError = fileSizeError(file, maxFileBytes);
  if (sizeError !== null) {
    throw new Error(sizeError);
  }
  return new Uint8Array(await file.arrayBuffer());
}

function sameBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) {
    return false;
  }
  for (let index = 0; index < left.byteLength; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

function triggerJsonDownload(backup: CreatedJsonBackup): void {
  const blob = new Blob([backup.canonical_json], { type: 'application/json;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.download = backup.file_name;
  anchor.href = objectUrl;
  anchor.hidden = true;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

function Counts({ counts }: { readonly counts: BackupCounts }) {
  const entries = Object.entries(counts).sort(([left], [right]) => left.localeCompare(right));
  return (
    <dl className="shift-summary">
      {entries.map(([key, count]) => (
        <div key={key}>
          <dt>{countLabel(key)}</dt>
          <dd>{count.toLocaleString('cs-CZ')}</dd>
        </div>
      ))}
    </dl>
  );
}

function Issues({
  announce = true,
  issues,
}: {
  readonly announce?: boolean;
  readonly issues: readonly BackupIssue[];
}) {
  if (issues.length === 0) {
    return <p>Nebyly nalezeny žádné problémy.</p>;
  }
  return (
    <div className="error-summary" role={announce ? 'alert' : undefined}>
      <strong>Nalezené problémy</strong>
      <ul>
        {issues.map((issue, index) => (
          <li key={`${issue.severity}:${issue.code}:${index}`} data-issue-code={issue.code}>
            {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function IntegrityDigest({ value }: { readonly value: string }) {
  return (
    <details className="inline-technical">
      <summary>Kontrolní údaj souboru</summary>
      <code>{value}</code>
    </details>
  );
}

export function BackupPanel({
  maxFileBytes,
  operations,
  reloadApplication = () => window.location.reload(),
}: BackupPanelProps) {
  const ids = useId();
  const fileLimit = useMemo(() => effectiveFileLimit(maxFileBytes), [maxFileBytes]);
  const [busy, setBusy] = useState<BusyOperation | null>(null);
  const [createdBackup, setCreatedBackup] = useState<CreatedJsonBackup | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [verification, setVerification] = useState<VerifiedCurrentJsonBackup | null>(null);
  const [restoreStage, setRestoreStage] = useState<RestoreStage | null>(null);
  const [explicitConfirmation, setExplicitConfirmation] = useState(false);
  const [reselectedBytes, setReselectedBytes] = useState<Uint8Array | null>(null);
  const [reselectedFileName, setReselectedFileName] = useState<string | null>(null);
  const [restoreOutcome, setRestoreOutcome] = useState<RestoreOutcome | null>(null);
  const panelHeading = useRef<HTMLHeadingElement>(null);
  const restoreOutcomeHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    panelHeading.current?.focus();
  }, []);

  useEffect(() => {
    if (restoreOutcome !== null) {
      restoreOutcomeHeading.current?.focus();
    }
  }, [restoreOutcome]);

  const blockingRestoreIssues =
    restoreStage?.preview.issues.some((issue) => issue.severity !== 'warning') ?? false;
  const verifiedDownloadedBackup =
    verification !== null &&
    verification.matches_current_data &&
    verification.issues.every((issue) => issue.severity === 'warning') &&
    createdBackup !== null &&
    verification.whole_file_sha256 === createdBackup.whole_file_sha256;
  const canRestore =
    restoreStage !== null &&
    !blockingRestoreIssues &&
    explicitConfirmation &&
    reselectedBytes !== null &&
    busy === null;

  async function createBackup(): Promise<void> {
    if (busy !== null) {
      return;
    }
    setBusy('create');
    setExportError(null);
    setCreatedBackup(null);
    setVerification(null);
    try {
      const backup = await operations.createJsonBackup();
      triggerJsonDownload(backup);
      setCreatedBackup(backup);
    } catch (error) {
      setExportError(errorDetail(error, 'Zálohu se nepodařilo vytvořit ani bezpečně stáhnout.'));
    } finally {
      setBusy(null);
    }
  }

  async function verifyDownloadedFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (file === undefined || createdBackup === null || busy !== null) {
      input.value = '';
      return;
    }
    setBusy('verify');
    setExportError(null);
    setVerification(null);
    try {
      const bytes = await readFileBytes(file, fileLimit);
      const result = await operations.verifyCurrentJsonBackup(bytes);
      setVerification(result);
    } catch (error) {
      setExportError(errorDetail(error, 'Vybranou zálohu se nepodařilo ověřit.'));
    } finally {
      input.value = '';
      setBusy(null);
    }
  }

  async function previewRestore(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (file === undefined || busy !== null) {
      input.value = '';
      return;
    }
    setBusy('preview');
    // A newer file selection always invalidates every destructive decision
    // made for an older file, even when the newer file turns out to be bad.
    setRestoreStage(null);
    setExplicitConfirmation(false);
    setReselectedBytes(null);
    setReselectedFileName(null);
    setRestoreOutcome(null);
    try {
      const bytes = await readFileBytes(file, fileLimit);
      const preview = await operations.previewJsonRestore(bytes);
      setRestoreStage({ fileName: file.name, preview, sourceBytes: bytes });
    } catch (error) {
      setRestoreOutcome({
        detail: errorDetail(error, 'Soubor se nepodařilo bezpečně připravit k obnově.'),
        kind: 'error',
        title: 'Obnovu nelze připravit',
      });
    } finally {
      input.value = '';
      setBusy(null);
    }
  }

  async function reselectRestoreFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (file === undefined || restoreStage === null || busy !== null) {
      input.value = '';
      return;
    }
    try {
      const bytes = await readFileBytes(file, fileLimit);
      if (!sameBytes(restoreStage.sourceBytes, bytes)) {
        setReselectedBytes(null);
        setReselectedFileName(null);
        setRestoreOutcome({
          detail:
            'Druhý soubor není přesně shodný se souborem použitým pro náhled. Data zůstala beze změny.',
          kind: 'error',
          title: 'Opětovný výběr nesouhlasí',
        });
        return;
      }
      setReselectedBytes(bytes);
      setReselectedFileName(file.name);
      setRestoreOutcome(null);
    } catch (error) {
      setReselectedBytes(null);
      setReselectedFileName(null);
      setRestoreOutcome({
        detail: errorDetail(error, 'Opětovně vybraný soubor se nepodařilo přečíst.'),
        kind: 'error',
        title: 'Opětovný výběr nelze ověřit',
      });
    } finally {
      input.value = '';
    }
  }

  async function restoreBackup(): Promise<void> {
    if (!canRestore || restoreStage === null || reselectedBytes === null || busy !== null) {
      setRestoreOutcome({
        detail:
          'Obnova vyžaduje náhled bez blokujících problémů, výslovné potvrzení a druhý výběr stejného souboru.',
        kind: 'error',
        title: 'Obnova zůstala zablokovaná',
      });
      return;
    }
    setBusy('restore');
    try {
      const result = await operations.restoreJsonBackup({
        expected_whole_file_sha256: restoreStage.preview.whole_file_sha256,
        explicit_confirmation: true,
        reselected_bytes: reselectedBytes,
      });
      setRestoreStage(null);
      setExplicitConfirmation(false);
      setReselectedBytes(null);
      setReselectedFileName(null);
      setCreatedBackup(null);
      setVerification(null);
      setExportError(null);
      setRestoreOutcome({
        datasetEpoch: result.replacement_dataset_epoch,
        kind: 'success',
        reopenRequired: result.reopen_required,
        title: result.reopen_required
          ? 'Obnova je zapsána, dokonči nové otevření'
          : 'Obnova dokončena',
      });
    } catch (error) {
      // The adapter consumes its preview authorization on every execution
      // attempt. Requiring a fresh preview keeps UI state aligned with it.
      setRestoreStage(null);
      setExplicitConfirmation(false);
      setReselectedBytes(null);
      setReselectedFileName(null);
      setRestoreOutcome({
        detail: `${errorDetail(
          error,
          'Obnovu se nepodařilo bezpečně dokončit.',
        )} Před dalším pokusem soubor znovu vyber pro nový náhled.`,
        kind: 'error',
        title: 'Obnova se nezdařila',
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="alpha-view backup-panel" aria-labelledby={`${ids}-heading`}>
      <h2 ref={panelHeading} id={`${ids}-heading`} tabIndex={-1}>
        Záloha a místní obnova
      </h2>
      <div className="safety-note" aria-labelledby={`${ids}-limitations-heading`}>
        <strong id={`${ids}-limitations-heading`}>Co záloha zahrnuje</strong>
        <span>
          Koncepty z automatického ukládání nelze exportovat. Pokud nějaký existuje, vytvoření
          zálohy se bezpečně zastaví chybou; koncept nejprve ulož nebo zahod.
        </span>
        <span>
          Místní obnova neaktivuje cloudový účet ani synchronizaci. Obnoví pouze data uložená v
          tomto zařízení.
        </span>
      </div>

      <section aria-labelledby={`${ids}-export-heading`}>
        <h3 id={`${ids}-export-heading`}>1. Vytvořit a ověřit JSON zálohu</h3>
        <p>
          Stáhni úplnou kanonickou JSON zálohu uložených dat. Maximální velikost souboru pro
          následné ověření je 32 MiB.
        </p>
        <div className="form-actions">
          <button
            type="button"
            className="primary-action touch-target"
            disabled={busy !== null}
            onClick={() => void createBackup()}
          >
            {busy === 'create' ? 'Vytvářím zálohu…' : 'Vytvořit a stáhnout JSON zálohu'}
          </button>
        </div>
        {exportError !== null ? (
          <div className="error-summary" role="alert">
            <strong>Zálohu nelze ověřit</strong>
            <p>{exportError}</p>
          </div>
        ) : null}
        {createdBackup !== null ? (
          <div aria-live="polite">
            <h4>Záloha byla předána ke stažení</h4>
            <p>
              Soubor: <strong>{createdBackup.file_name}</strong>
            </p>
            <IntegrityDigest value={createdBackup.whole_file_sha256} />
            <Counts counts={createdBackup.counts} />
            <div className="form-field">
              <label htmlFor={`${ids}-verify-file`}>Znovu vybrat právě stažený soubor</label>
              <p className="field-description" id={`${ids}-verify-description`}>
                Ověření samostatně zkontroluje celý soubor proti aktuálně uloženým datům.
              </p>
              <input
                id={`${ids}-verify-file`}
                aria-describedby={`${ids}-verify-description`}
                className="text-input touch-target"
                type="file"
                accept=".json,application/json"
                disabled={busy !== null}
                onChange={(event) => void verifyDownloadedFile(event)}
              />
            </div>
          </div>
        ) : null}
        {verification !== null ? (
          <div
            className={verifiedDownloadedBackup ? undefined : 'error-summary'}
            role={verifiedDownloadedBackup ? 'status' : 'alert'}
          >
            <h4>{verifiedDownloadedBackup ? 'Stažená záloha je ověřena' : 'Ověření nesouhlasí'}</h4>
            <p>
              {verifiedDownloadedBackup
                ? 'Znovu vybraný soubor je byte-shodný se staženou zálohou a přesně pokrývá aktuálně uložená data.'
                : 'Vybraný soubor není právě stažená záloha nebo přesně nepokrývá aktuálně uložená data.'}
            </p>
            <IntegrityDigest value={verification.whole_file_sha256} />
            <Counts counts={verification.counts} />
            <Issues announce={false} issues={verification.issues} />
          </div>
        ) : null}
      </section>

      <section aria-labelledby={`${ids}-restore-heading`}>
        <h3 id={`${ids}-restore-heading`}>2. Náhled a úplné nahrazení dat</h3>
        <p>
          První výběr pouze ověří soubor a zobrazí náhled. Aktivní data v tomto kroku zůstávají beze
          změny.
        </p>
        <div className="form-field">
          <label htmlFor={`${ids}-preview-file`}>Vybrat JSON zálohu pro náhled obnovy</label>
          <p className="field-description" id={`${ids}-preview-description`}>
            Soubor větší než 32 MiB bude odmítnut ještě před načtením obsahu.
          </p>
          <input
            id={`${ids}-preview-file`}
            aria-describedby={`${ids}-preview-description`}
            className="text-input touch-target"
            type="file"
            accept=".json,application/json"
            disabled={busy !== null}
            onChange={(event) => void previewRestore(event)}
          />
        </div>

        {restoreStage !== null ? (
          <div aria-labelledby={`${ids}-preview-result-heading`}>
            <h4 id={`${ids}-preview-result-heading`}>Náhled souboru {restoreStage.fileName}</h4>
            <p role="status" aria-live="polite" aria-atomic="true">
              Náhled byl dokončen. Aktivní data zůstala beze změny.
            </p>
            <IntegrityDigest value={restoreStage.preview.whole_file_sha256} />
            <Counts counts={restoreStage.preview.counts} />
            {restoreStage.preview.duplicate_entity_ids.length > 0 ? (
              <div className="error-summary" role="status">
                <strong>Záznamy nalezené i v aktuálních datech</strong>
                <p>
                  Režim Nahradit nevytváří druhé kopie, ale úplně nahradí aktuální data. Shodné
                  záznamy:{' '}
                  {restoreStage.preview.duplicate_entity_ids.length.toLocaleString('cs-CZ')}.
                </p>
              </div>
            ) : (
              <p>V aktuálním profilu nebyly nalezeny shodné identity.</p>
            )}
            <Issues issues={restoreStage.preview.issues} />
            {blockingRestoreIssues ? (
              <p className="field-error" role="alert">
                Náhled obsahuje konflikt nebo chybu. Nahrazení dat zůstává zablokované.
              </p>
            ) : null}

            <label>
              <input
                type="checkbox"
                className="touch-target"
                checked={explicitConfirmation}
                disabled={busy !== null || blockingRestoreIssues}
                onChange={(event) => setExplicitConfirmation(event.currentTarget.checked)}
              />{' '}
              Rozumím, že obnova úplně nahradí aktuálně uložená data v tomto místním profilu.
            </label>

            <div className="form-field">
              <label htmlFor={`${ids}-reselect-file`}>
                Znovu vybrat stejný soubor pro destruktivní obnovu
              </label>
              <p className="field-description" id={`${ids}-reselect-description`}>
                Musí jít o přesně shodný soubor jako při náhledu. První výběr se znovu nepoužije.
              </p>
              <input
                id={`${ids}-reselect-file`}
                aria-describedby={`${ids}-reselect-description`}
                className="text-input touch-target"
                type="file"
                accept=".json,application/json"
                disabled={busy !== null || blockingRestoreIssues}
                onChange={(event) => void reselectRestoreFile(event)}
              />
            </div>
            {reselectedFileName !== null ? (
              <p role="status">Přesně shodný soubor byl znovu vybrán: {reselectedFileName}</p>
            ) : null}
            <div className="form-actions">
              <button
                type="button"
                className="danger-action touch-target"
                disabled={!canRestore}
                onClick={() => void restoreBackup()}
              >
                {busy === 'restore'
                  ? 'Nahrazuji všechna data…'
                  : 'Nahradit všechna uložená data touto zálohou'}
              </button>
            </div>
          </div>
        ) : null}

        {restoreOutcome !== null ? (
          <div
            className={restoreOutcome.kind === 'error' ? 'error-summary' : undefined}
            role={restoreOutcome.kind === 'error' ? 'alert' : 'status'}
            aria-live={restoreOutcome.kind === 'success' ? 'polite' : undefined}
          >
            <h4 ref={restoreOutcomeHeading} tabIndex={-1}>
              {restoreOutcome.title}
            </h4>
            {restoreOutcome.kind === 'error' ? (
              <p>{restoreOutcome.detail}</p>
            ) : (
              <>
                <p>Data byla nahrazena až po dvojím výběru přesně shodného souboru.</p>
                <details className="inline-technical">
                  <summary>Kontrolní údaj obnovy</summary>
                  <code>{restoreOutcome.datasetEpoch}</code>
                </details>
                {restoreOutcome.reopenRequired ? (
                  <div className="form-actions">
                    <p>
                      Obnova je bezpečně dokončená, ale toto připojení bylo uzavřeno. Před další
                      prací aplikaci znovu načti.
                    </p>
                    <button
                      type="button"
                      className="primary-action touch-target"
                      onClick={reloadApplication}
                    >
                      Dokončit obnovu novým načtením aplikace
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </section>
    </section>
  );
}
